import { and, asc, eq, gt, isNull, sql } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import type {
  LearnedGraphemeRow,
  LearnedStudyCardRow,
  LearningStatsReviewHistoryData,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import { runLearningStatsQueryStep } from "#run-learning-stats-query-step";
import {
  createReviewCountsAccumulator,
  createStageHistoryAccumulator,
} from "#streaming-review-history";

export const LEARNING_STATS_HISTORY_PAGE_SIZE = 256;

type ReviewCursor = { reviewedAt: string; rowId: number };
const reviews = actorSchema.userCardReview;
const logs = actorSchema.fsrsCardReviewLog;

/**
 * The existing live reviewed_at index includes rowid as its tie breaker. Paging
 * that order avoids both OFFSET rescans and a lifetime-sized native temp sort.
 * Rowid also preserves the old findMany order for equal review timestamps.
 */
export function learningStatsUserReviewPageQuery(
  actorDb: ActorDatabase,
  after: ReviewCursor | null,
) {
  const reviewRowId = sql<number>`${reviews}._rowid_`;
  return actorDb(import.meta.location)
    .select({
      cardId: reviews.cardId,
      reviewedAt: reviews.reviewedAt,
      rowId: reviewRowId,
      scheduledDays: logs.scheduledDays,
    })
    .from(reviews)
    .leftJoin(logs, and(eq(reviews.fsrsCardReviewLogId, logs.id), isNull(logs.deletedAt)))
    .where(
      and(
        isNull(reviews.deletedAt),
        after === null
          ? undefined
          : sql`(${reviews.reviewedAt}, ${reviewRowId}) > (${after.reviewedAt}, ${after.rowId})`,
      ),
    )
    .orderBy(asc(reviews.reviewedAt), asc(reviewRowId))
    .limit(LEARNING_STATS_HISTORY_PAGE_SIZE);
}

function assertNotCancelled(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new Error("Learning Stats history read was cancelled");
  }
}

async function readUserReviews({
  actorDb,
  onReview,
  signal,
}: {
  actorDb: ActorDatabase;
  onReview: (review: { cardId: string; reviewedAt: string; scheduledDays: number | null }) => void;
  signal?: AbortSignal;
}) {
  let after: ReviewCursor | null = null;
  while (true) {
    assertNotCancelled(signal);
    const page = await runLearningStatsQueryStep("review history page", async () =>
      learningStatsUserReviewPageQuery(actorDb, after),
    );
    assertNotCancelled(signal);
    for (const row of page) {
      onReview(row);
    }
    const last = page.at(-1);
    if (last === undefined || page.length < LEARNING_STATS_HISTORY_PAGE_SIZE) {
      return;
    }
    after = { reviewedAt: last.reviewedAt, rowId: last.rowId };
  }
}

/** No raw page enters QueryCache; only O(days + learned cards) survives each page. */
export async function readStreamingReviewHistory({
  actorDb,
  cards,
  now = new Date(),
  signal,
}: {
  actorDb: ActorDatabase;
  cards: readonly LearnedStudyCardRow[];
  now?: Date;
  signal?: AbortSignal;
}): Promise<LearningStatsReviewHistoryData> {
  const counts = createReviewCountsAccumulator(now);
  const stages = createStageHistoryAccumulator(cards, now);
  await readUserReviews({
    actorDb,
    onReview(row) {
      counts.addReview(row.reviewedAt);
      stages.addReview(row);
    },
    signal,
  });

  // Retention historically counts active FSRS logs even when their user-card
  // review was deleted or is missing. A separate projected scan preserves that.
  const logRowId = sql<number>`${logs}._rowid_`;
  let after: number | null = null;
  while (true) {
    assertNotCancelled(signal);
    const page = await runLearningStatsQueryStep("retention history page", async () =>
      actorDb(import.meta.location)
        .select({ rating: logs.rating, review: logs.review, rowId: logRowId })
        .from(logs)
        .where(and(isNull(logs.deletedAt), after === null ? undefined : gt(logRowId, after)))
        .orderBy(asc(logRowId))
        .limit(LEARNING_STATS_HISTORY_PAGE_SIZE),
    );
    assertNotCancelled(signal);
    for (const log of page) {
      counts.addLog(log);
    }
    const last = page.at(-1);
    if (last === undefined || page.length < LEARNING_STATS_HISTORY_PAGE_SIZE) {
      break;
    }
    after = last.rowId;
  }
  return { ...counts.finish(), characterStageTimeline: stages.finish().characterStageTimeline };
}

/** Catalog loading stays independent of actor-only history, without retaining raw reviews. */
export async function readStreamingCoverageHistory({
  actorDb,
  cards,
  now = new Date(),
  signal,
}: {
  actorDb: ActorDatabase;
  cards: readonly LearnedGraphemeRow[];
  now?: Date;
  signal?: AbortSignal;
}) {
  const stages = createStageHistoryAccumulator(
    cards,
    now,
    new Map(cards.map((card) => [card.id, Math.max(0, card.frequencyPerMillion ?? 0)])),
  );
  await readUserReviews({ actorDb, onReview: stages.addReview, signal });
  return stages.finish().coverageStageTimeline;
}
